import { ScrollArea } from '@/components/shadcn/scroll-area'
import { ComponentHeader } from '../shared/ComponentHeader'
import { ComponentTitle } from '../shared/ComponentTitle'
import type { AgentStep, ChatMessage, ChatUISchema, LiveChatDataSchema } from '../../Chat/types'
import { forwardRef, useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react'
import { ChatContainer } from '../../Chat/ChatContainer'
import { LiveChatMessageList } from '../../LiveChat/LiveChatMessageList'
import { LiveChatInput } from '../../LiveChat/LiveChatInput'
import { ChevronDown, RefreshCw, Plus, Loader2, LogOut } from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import { ComponentActionsContainer } from '../shared/ComponentActionsContainer'
import { getComponentHeights } from '../utils'
import { useAppContext } from '../../../hooks/useAppContext'
import { useSessionStorage } from '@/hooks'
import { ENDPOINTS } from '@/util'
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr'
import { clearLiveChatSessionData, disconnectLiveChatSession, sendLiveChatMessage } from '../../Chat/utils'
import { useQueryClient } from '@tanstack/react-query'

// Connection state types for better UI feedback
type ConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected' | 'failed'

interface LiveChatContentProps {
  uiSchema: ChatUISchema
  connectionData: LiveChatDataSchema
  tileFilters?: React.ReactNode
  onNewChat?: () => void
}

export const LiveChatContent = forwardRef<HTMLDivElement, LiveChatContentProps>(function LiveChatContent(
  { uiSchema, connectionData, tileFilters, onNewChat },
  ref
) {
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState(() => scrollAreaRef.current?.clientHeight)
  const [showScrollButton, setShowScrollButton] = useState(false)
  const { title, description, samplePrompts, emphasizeHeader } = uiSchema
  const { appKey, workspaceId } = useAppContext()
  const [token] = useSessionStorage('token', '')
  const queryClient = useQueryClient()
  const [connection, setConnection] = useState<HubConnection | null>(null)
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('connecting')
  const [disconnectedError, setDisconnectedError] = useState<string>('')
  const [sendError, setSendError] = useState<string>('')
  const [refreshKey, setRefreshKey] = useState<number>(0)
  const [chatId, setChatId] = useState<string | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [thinkingByMessageId, setThinkingByMessageId] = useState<Record<string, string>>({})
  const [responseStartedById, setResponseStartedById] = useState<Record<string, boolean>>({})
  const [agentStepsByMessageId, setAgentStepsByMessageId] = useState<Record<string, AgentStep[]>>({})
  const [reconnectAttempt, setReconnectAttempt] = useState<number>(0)
  const [isAwaitingAssistant, setIsAwaitingAssistant] = useState<boolean>(false)
  const currentAssistantIdRef = useRef<string>('')
  const requestIdRef = useRef<string>('')
  const requestAttemptRef = useRef<number>(0)
  const requestTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const preservedChatIdRef = useRef<string | null>(null)
  // Track connection attempt to prevent React Strict Mode double-mount race conditions
  const connectionAttemptRef = useRef<number>(0)
  const hasHeader = !!title && !!tileFilters
  const { contentHeight } = getComponentHeights(emphasizeHeader, hasHeader)

  // Derived state for UI
  const connecting = connectionStatus === 'connecting'
  const isReconnecting = connectionStatus === 'reconnecting'

  // Log when we get connection data to help debug
  useEffect(() => {
    console.log('[LiveChat] Connection data received:', {
      requestId: connectionData.requestId,
      taskId: connectionData.taskId,
      isExistingSession: connectionData.isExistingSession,
      success: connectionData.success,
    })
  }, [connectionData])

  useLayoutEffect(() => {
    setHeight(scrollAreaRef.current?.clientHeight)
  }, [])

  // Function to scroll to bottom
  const scrollToBottom = (smooth = true) => {
    if (scrollAreaRef.current) {
      const scrollElement = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]')
      if (scrollElement) {
        try {
          ;(scrollElement as HTMLElement).scrollTo({
            top: (scrollElement as HTMLElement).scrollHeight,
            behavior: smooth ? 'smooth' : 'auto',
          })
        } catch {
          // fallback
          ;(scrollElement as HTMLElement).scrollTop = (scrollElement as HTMLElement).scrollHeight
        }
      }
    }
  }

  // Function to check if scrolled to bottom
  const checkScrollPosition = () => {
    if (scrollAreaRef.current) {
      const scrollElement = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]')
      if (scrollElement) {
        const isAtBottom = scrollElement.scrollTop + scrollElement.clientHeight >= scrollElement.scrollHeight - 10
        setShowScrollButton(!isAtBottom)
      }
    }
  }

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom(true)
  }, [messages, connecting])

  // Set up scroll event listener
  useEffect(() => {
    const scrollElement = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]')
    if (scrollElement) {
      scrollElement.addEventListener('scroll', checkScrollPosition)
      // Initial check
      checkScrollPosition()

      return () => {
        scrollElement.removeEventListener('scroll', checkScrollPosition)
      }
    }
  }, [scrollAreaRef])

  // Smoothly follow thinking block growth: when thinking text for the active assistant message grows,
  // nudge the viewport to bottom smoothly so it feels like it "scrolls a line" each time.
  const prevThinkingLenRef = useRef<number>(0)
  const prevThinkingMsgIdRef = useRef<string>('')
  useEffect(() => {
    const id = currentAssistantIdRef.current
    if (!id) {
      prevThinkingLenRef.current = 0
      prevThinkingMsgIdRef.current = ''
      return
    }
    const isThinking = !responseStartedById[id] && Boolean(thinkingByMessageId[id]?.length)
    if (!isThinking) {
      prevThinkingLenRef.current = 0
      prevThinkingMsgIdRef.current = ''
      return
    }
    const len = thinkingByMessageId[id]?.length ?? 0
    if (prevThinkingMsgIdRef.current !== id || len > prevThinkingLenRef.current) {
      scrollToBottom(true)
      prevThinkingMsgIdRef.current = id
      prevThinkingLenRef.current = len
    }
  }, [thinkingByMessageId, responseStartedById])

  // SignalR connection and behavior
  // Note: We use the proxied hub URL (ENDPOINTS.DOTNET) for browser access to avoid CORS issues
  // The backend's hubUrl is for server-to-server communication (worker)
  const {
    requestId: backendRequestId,
    chatId: backendChatId,
    success: workerStarted,
    sendMessageEventId,
  } = connectionData
  // Use the proxied SignalR endpoint for browser access
  const hubUrl = `${ENDPOINTS.DOTNET}/rest/collaboration-hub`
  const generateRequestId = () => globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`

  // Determine if we're using message-based flow (new architecture)
  // In message-based flow:
  // - Workers are already running and listening to the queue
  // - We have a chatId for SignalR channel from the backend
  // - We have sendMessageEventId to create queue tasks when user sends messages
  // - No requestId/taskId needed upfront since workers are pre-started
  const isMessageBasedFlow = Boolean(backendChatId && sendMessageEventId)

  // For message-based flow, we can connect as long as we have chatId
  // For legacy flow, we need requestId and workerStarted
  const canConnect = useMemo(() => {
    if (isMessageBasedFlow) {
      // Message-based: just need chatId and token
      return Boolean(token && appKey && backendChatId)
    }
    // Legacy: need requestId and workerStarted
    return Boolean(token && appKey && backendRequestId && workerStarted)
  }, [token, appKey, backendRequestId, backendChatId, workerStarted, isMessageBasedFlow])

  useEffect(() => {
    console.log('[LiveChat] Connection effect running', {
      canConnect,
      appKey,
      backendRequestId,
      backendChatId,
      refreshKey,
      workerStarted,
      isMessageBasedFlow,
    })

    if (!canConnect) {
      console.log('[LiveChat] Cannot connect - missing required data', {
        hasToken: !!token,
        hasAppKey: !!appKey,
        hasRequestId: !!backendRequestId,
        hasChatId: !!backendChatId,
        workerStarted,
        isMessageBasedFlow,
      })
      return
    }

    // Custom reconnection delays with exponential backoff (in milliseconds)
    // 0, 2s, 5s, 10s, 15s, 30s - then repeats 30s
    const reconnectDelays = [0, 2000, 5000, 10000, 15000, 30000]

    const connectionBuilder = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => token,
        withCredentials: true,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          // Use custom delays, then fall back to 30s for subsequent attempts
          const delay = reconnectDelays[retryContext.previousRetryCount] ?? 30000
          console.log(`[LiveChat] Reconnect attempt ${retryContext.previousRetryCount + 1}, delay: ${delay}ms`)
          return delay
        },
      })
      .configureLogging(LogLevel.Information)

    const newConnection = connectionBuilder.build()
    let didStart = false

    const clearPendingTimeout = () => {
      if (requestTimeoutRef.current) {
        clearTimeout(requestTimeoutRef.current)
        requestTimeoutRef.current = null
      }
    }

    const scheduleRequestTimeout = (invokeRequest: () => void) => {
      clearPendingTimeout()
      requestTimeoutRef.current = setTimeout(() => {
        // Retry up to 10 times to give the worker time to connect (increased from 5)
        if (requestAttemptRef.current < 10) {
          requestAttemptRef.current += 1
          console.log(`[LiveChat] Chat request retry ${requestAttemptRef.current}/10`)
          // Keep using the same backend requestId - don't generate a new one
          // The worker is listening for this specific requestId
          invokeRequest()
          scheduleRequestTimeout(invokeRequest)
        } else {
          setDisconnectedError('Cannot connect (timeout) - worker may not be ready')
          setConnectionStatus('failed')
        }
      }, 5_000) // Retry every 5 seconds
    }

    const handleChatCreated = (createdChatId: string, requestId: string) => {
      if (requestId !== requestIdRef.current) return
      console.log(`[LiveChat] Chat created: ${createdChatId} for request ${requestId}`)
      setChatId(createdChatId)
      preservedChatIdRef.current = createdChatId
      setConnectionStatus('connected')
      setDisconnectedError('')
      setReconnectAttempt(0)
      clearPendingTimeout()
    }

    const handleNoListeners = (requestId: string) => {
      if (requestId !== requestIdRef.current) return
      setDisconnectedError('No listeners available for this app')
      setConnectionStatus('failed')
      clearPendingTimeout()
    }

    const handleReceiveMessage = (rawMessage: string) => {
      console.log('[LiveChat] Received message:', rawMessage)
      let assistantId = currentAssistantIdRef.current
      let parsed: unknown
      try {
        parsed = JSON.parse(rawMessage)
        console.log('[LiveChat] Parsed message:', parsed)
      } catch {
        parsed = null
        console.log('[LiveChat] Message is plain text (not JSON)')
      }

      if (parsed && typeof parsed === 'object') {
        const data = parsed as {
          response?: string
          thinking?: string
          done?: boolean
          stepType?: string
          stepNumber?: number
          toolName?: string
          toolArgs?: Record<string, unknown>
          toolResult?: string
        }

        // Helper to ensure assistant message exists
        const ensureAssistantMessage = () => {
          if (!assistantId) {
            assistantId = generateRequestId()
            currentAssistantIdRef.current = assistantId
            const now = new Date().toISOString()
            console.log('[LiveChat] Created new assistant message:', assistantId)
            setMessages((prev) => [...prev, { id: assistantId!, role: 'assistant', content: '', timestamp: now }])
            setResponseStartedById((prev) => ({ ...prev, [assistantId!]: false }))
            setAgentStepsByMessageId((prev) => ({ ...prev, [assistantId!]: [] }))
          }
          return assistantId!
        }

        // Agent step (tool_call, tool_result, agent_step, thinking)
        if (data.stepType) {
          const msgId = ensureAssistantMessage()
          console.log('[LiveChat] Processing agent step:', data.stepType, data.stepNumber)
          const newStep: AgentStep = {
            stepNumber: data.stepNumber ?? 0,
            stepType: data.stepType as AgentStep['stepType'],
            toolName: data.toolName,
            toolArgs: data.toolArgs,
            toolResult: data.toolResult,
            timestamp: new Date().toISOString(),
          }
          setAgentStepsByMessageId((prev) => ({
            ...prev,
            [msgId]: [...(prev[msgId] || []), newStep],
          }))
          return
        }

        // Thinking only (legacy format without stepType)
        if (typeof data.thinking === 'string' && (!data.response || data.response.length === 0)) {
          console.log('[LiveChat] Processing thinking chunk:', data.thinking.substring(0, 50))
          const msgId = ensureAssistantMessage()
          setThinkingByMessageId((prev) => ({
            ...prev,
            [msgId]: (prev[msgId] || '') + data.thinking,
          }))
          return
        }

        // Response token
        if (typeof data.response === 'string' && data.response.length > 0) {
          const responseChunk = data.response
          console.log('[LiveChat] Processing response chunk:', responseChunk.substring(0, 50))
          const msgId = ensureAssistantMessage()
          setResponseStartedById((prev) => ({ ...prev, [msgId]: true }))
          setMessages((prev) => {
            const index = prev.findIndex((m) => m.id === msgId)
            if (index >= 0) {
              const updated = prev.slice()
              const target = updated[index]
              updated[index] = { ...target, content: target.content + responseChunk }
              console.log('[LiveChat] Updated message content, total length:', updated[index].content.length)
              return updated
            }
            console.log('[LiveChat] Adding new message with response')
            return [
              ...prev,
              { id: msgId, role: 'assistant', content: responseChunk, timestamp: new Date().toISOString() },
            ]
          })
        }

        // Done flag
        if (data.done === true) {
          console.log('[LiveChat] Received done flag, marking response complete')
          setIsAwaitingAssistant(false)
          // Reset current assistant ID so next message creates a new one
          currentAssistantIdRef.current = ''
        }
        return
      }

      // Plain text token (fallback for non-JSON messages)
      console.log('[LiveChat] Processing plain text message:', rawMessage.substring(0, 50))
      if (!assistantId) {
        assistantId = generateRequestId()
        currentAssistantIdRef.current = assistantId
        const now = new Date().toISOString()
        console.log('[LiveChat] Created new assistant message for plain text:', assistantId)
        setMessages((prev) => [...prev, { id: assistantId!, role: 'assistant', content: '', timestamp: now }])
      }
      setResponseStartedById((prev) => ({ ...prev, [assistantId!]: true }))
      setMessages((prev) => {
        const index = prev.findIndex((m) => m.id === assistantId)
        if (index >= 0) {
          const updated = prev.slice()
          const target = updated[index]
          updated[index] = { ...target, content: target.content + rawMessage }
          return updated
        }
        return [
          ...prev,
          { id: assistantId!, role: 'assistant', content: rawMessage, timestamp: new Date().toISOString() },
        ]
      })
    }

    const handlePeerDisconnected = (disconnectedChatId: string) => {
      // Only handle if it's our chat or we don't have a chat yet
      if (chatId && disconnectedChatId !== chatId) return
      console.log(`[LiveChat] Peer disconnected for chat ${disconnectedChatId}, will attempt reconnection`)
      // Don't immediately show error - the worker has a grace period
      // Show reconnecting state instead
      setConnectionStatus('reconnecting')
      setDisconnectedError('')
    }

    // Register handlers with both PascalCase and lowercase to handle different SignalR configurations
    // Some servers send lowercase method names (e.g., 'chatcreated'), others send PascalCase ('ChatCreated')
    newConnection.on('ChatCreated', handleChatCreated)
    newConnection.on('chatcreated', handleChatCreated)
    newConnection.on('NoListeners', handleNoListeners)
    newConnection.on('nolisteners', handleNoListeners)
    newConnection.on('ReceiveMessage', handleReceiveMessage)
    newConnection.on('receivemessage', handleReceiveMessage)
    newConnection.on('PeerDisconnected', handlePeerDisconnected)
    newConnection.on('peerdisconnected', handlePeerDisconnected)

    // Handle reconnecting state
    newConnection.onreconnecting((error) => {
      console.log('[LiveChat] Connection reconnecting...', error)
      setConnectionStatus('reconnecting')
      setReconnectAttempt((prev) => prev + 1)
    })

    // Handle reconnected state
    newConnection.onreconnected(async (connectionId) => {
      console.log(`[LiveChat] Connection reconnected with id: ${connectionId}`)
      try {
        if (!appKey) return
        setConnectionStatus('connecting')
        await newConnection.invoke('JoinAppKeyChannel', appKey, 'frontend')
        requestAttemptRef.current = 0
        // Use the same backend requestId on reconnection
        requestIdRef.current = backendRequestId ?? ''
        const invokeRequest = () => newConnection.invoke('RequestChat', appKey, requestIdRef.current, workspaceId ?? -1)
        await invokeRequest()
        scheduleRequestTimeout(invokeRequest)
      } catch (_err) {
        console.error('[LiveChat] Reconnection error:', _err)
        setDisconnectedError('Reconnection error - please refresh')
        setConnectionStatus('failed')
      }
    })

    // Handle connection closed
    newConnection.onclose((error) => {
      console.log('[LiveChat] Connection closed', error)
      // Only show disconnected if we're not already in a terminal state
      if (connectionStatus !== 'failed') {
        setConnectionStatus('disconnected')
        setDisconnectedError('Connection lost - attempting to reconnect...')
      }
    })

    // Track if this effect is still active (not cleaned up)
    let isActive = true
    // Increment and capture the current connection attempt to detect superseded attempts
    connectionAttemptRef.current += 1
    const thisAttempt = connectionAttemptRef.current

    // Helper to check if this attempt is still valid
    const isStillValid = () => isActive && thisAttempt === connectionAttemptRef.current

    const startAsync = async () => {
      try {
        setConnectionStatus('connecting')
        await newConnection.start()

        // Check if effect was cleaned up or superseded by a newer attempt (React Strict Mode)
        if (!isStillValid()) {
          console.log('[LiveChat] Connection attempt superseded or cleaned up, stopping connection')
          newConnection.stop().catch(() => {})
          return
        }

        didStart = true
        setConnection(newConnection)
        console.log('[LiveChat] SignalR connection started')

        // Check if we're using message-based flow (new architecture)
        if (isMessageBasedFlow && backendChatId) {
          // Message-based flow: Join the specific chat channel directly
          // The chatId is created by the backend and we just listen for responses
          console.log('[LiveChat] Using message-based flow, joining chat channel:', backendChatId)
          await newConnection.invoke('JoinChatChannel', backendChatId, 'frontend')

          if (!isStillValid()) {
            console.log('[LiveChat] Connection attempt superseded during channel join, stopping')
            newConnection.stop().catch(() => {})
            return
          }

          // Set the chat ID directly - no need to wait for ChatCreated event
          setChatId(backendChatId)
          preservedChatIdRef.current = backendChatId
          setConnectionStatus('connected')
          setDisconnectedError('')
          clearPendingTimeout()
          console.log('[LiveChat] Connected to chat channel (message-based flow):', backendChatId)
        } else {
          // Legacy session-based flow: Join app channel then request a 1-to-1 chat
          console.log('[LiveChat] Using session-based flow')
          await newConnection.invoke('JoinAppKeyChannel', appKey, 'frontend')

          // Check again after invoke
          if (!isStillValid()) {
            console.log('[LiveChat] Connection attempt superseded during channel join, stopping')
            newConnection.stop().catch(() => {})
            return
          }

          requestAttemptRef.current = 0
          // Use the backend requestId - this matches what the worker is listening for
          requestIdRef.current = backendRequestId ?? ''
          const invokeRequest = () =>
            newConnection.invoke('RequestChat', appKey, requestIdRef.current, workspaceId ?? -1)
          await invokeRequest()
          scheduleRequestTimeout(invokeRequest)
        }
      } catch (_err) {
        // Ignore errors if cleaned up or superseded
        if (!isStillValid()) return
        console.error('[LiveChat] Failed to connect:', _err)
        setDisconnectedError('Failed to connect')
        setConnectionStatus('failed')
      }
    }

    startAsync()

    return () => {
      // Mark effect as inactive FIRST
      isActive = false

      // Remove both PascalCase and lowercase handlers
      newConnection.off('ChatCreated', handleChatCreated)
      newConnection.off('chatcreated', handleChatCreated)
      newConnection.off('NoListeners', handleNoListeners)
      newConnection.off('nolisteners', handleNoListeners)
      newConnection.off('ReceiveMessage', handleReceiveMessage)
      newConnection.off('receivemessage', handleReceiveMessage)
      newConnection.off('PeerDisconnected', handlePeerDisconnected)
      newConnection.off('peerdisconnected', handlePeerDisconnected)
      if (requestTimeoutRef.current) clearTimeout(requestTimeoutRef.current)

      // Always try to stop the connection - if start() is pending, stop() will cancel it
      // This prevents orphaned connections when React Strict Mode remounts
      if (newConnection.state !== HubConnectionState.Disconnected) {
        console.log('[LiveChat] Cleanup: stopping connection')
        newConnection.stop().catch(() => {})
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canConnect, appKey, backendRequestId, backendChatId, isMessageBasedFlow, refreshKey])

  // Streaming state is controlled by 'done' from server; avoid heuristic timers that may race tokens.

  // Function to manually refresh the SignalR connection (reconnect to same session)
  const refreshConnection = useCallback(async () => {
    console.log('[LiveChat] Manual refresh requested', {
      hasConnection: !!connection,
      connectionState: connection?.state,
      currentRequestId: requestIdRef.current,
      backendRequestId,
      currentRefreshKey: refreshKey,
    })
    // Clear any pending request timeout FIRST to prevent race conditions
    if (requestTimeoutRef.current) {
      console.log('[LiveChat] Clearing pending timeout')
      clearTimeout(requestTimeoutRef.current)
      requestTimeoutRef.current = null
    }
    // Stop existing connection if any and wait for it to complete
    if (connection && connection.state !== HubConnectionState.Disconnected) {
      console.log('[LiveChat] Stopping existing connection')
      try {
        await connection.stop()
        console.log('[LiveChat] Connection stopped successfully')
      } catch (err) {
        console.log('[LiveChat] Connection stop error (ignored):', err)
      }
    }
    // Reset ALL state and refs for a clean reconnection
    setConnection(null)
    setChatId(null)
    setDisconnectedError('')
    setConnectionStatus('connecting')
    setReconnectAttempt(0)
    // Reset all refs to initial state
    setIsAwaitingAssistant(false)
    currentAssistantIdRef.current = ''
    requestIdRef.current = ''
    requestAttemptRef.current = 0
    // Increment refreshKey to trigger the useEffect
    console.log('[LiveChat] Incrementing refreshKey to trigger reconnection')
    setRefreshKey((prev) => prev + 1)
  }, [connection, backendRequestId, refreshKey])

  // Function to start a completely new chat session
  // This clears the session storage and notifies parent to reset
  const startNewChat = useCallback(async () => {
    console.log('[LiveChat] Starting new chat session')
    // Clear any pending request timeout FIRST to prevent race conditions
    if (requestTimeoutRef.current) {
      clearTimeout(requestTimeoutRef.current)
      requestTimeoutRef.current = null
    }
    // Stop existing connection if any and wait for it to complete
    if (connection && connection.state !== HubConnectionState.Disconnected) {
      try {
        await connection.stop()
      } catch {
        // Ignore stop errors
      }
    }
    // Clear session storage
    clearLiveChatSessionData()
    // Reset all state including messages
    setConnection(null)
    setChatId(null)
    setDisconnectedError('')
    setConnectionStatus('connecting')
    setReconnectAttempt(0)
    setMessages([])
    setThinkingByMessageId({})
    setResponseStartedById({})
    setAgentStepsByMessageId({})
    // Reset all refs to initial state
    setIsAwaitingAssistant(false)
    currentAssistantIdRef.current = ''
    requestIdRef.current = ''
    requestAttemptRef.current = 0
    preservedChatIdRef.current = null
    // Invalidate the query
    queryClient.invalidateQueries({ queryKey: ['live-chat-schema'] })
    // Notify parent to show start form again
    onNewChat?.()
  }, [connection, queryClient, onNewChat])

  // State for disconnect loading
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const { projectId } = useAppContext()

  // Function to disconnect from the current chat session
  // Sends a disconnect event to the SignalR hub so the worker destroys the session
  const disconnectChat = useCallback(async () => {
    console.log('[LiveChat] Disconnecting from chat session')
    setIsDisconnecting(true)

    try {
      // Clear any pending request timeout FIRST
      if (requestTimeoutRef.current) {
        clearTimeout(requestTimeoutRef.current)
        requestTimeoutRef.current = null
      }

      // Send disconnect event to the SignalR hub before stopping connection
      // This notifies the worker to destroy the session and clear the chat bot
      if (connection && connection.state === HubConnectionState.Connected && chatId) {
        try {
          console.log('[LiveChat] Sending DisconnectChat event to hub for chatId:', chatId)
          await connection.invoke('DisconnectChat', chatId)
          console.log('[LiveChat] DisconnectChat event sent successfully')
        } catch (err) {
          console.warn('[LiveChat] Failed to send DisconnectChat event:', err)
          // Continue with disconnect even if hub notification fails
        }
      }

      // Stop the SignalR connection
      if (connection && connection.state !== HubConnectionState.Disconnected) {
        try {
          await connection.stop()
        } catch {
          // Ignore stop errors
        }
      }

      // Call backend to clean up Redis session
      if (appKey && projectId) {
        const result = await disconnectLiveChatSession({ appKey, projectId })
        console.log('[LiveChat] Backend disconnect result:', result)
      }

      // Clear local state
      clearLiveChatSessionData()
      setConnection(null)
      setChatId(null)
      setDisconnectedError('')
      setConnectionStatus('connecting')
      setReconnectAttempt(0)
      setMessages([])
      setThinkingByMessageId({})
      setResponseStartedById({})
      setAgentStepsByMessageId({})

      // Reset refs
      setIsAwaitingAssistant(false)
      currentAssistantIdRef.current = ''
      requestIdRef.current = ''
      requestAttemptRef.current = 0
      preservedChatIdRef.current = null

      // Invalidate query to refetch (will auto-create new session on page reload)
      queryClient.invalidateQueries({ queryKey: ['live-chat-schema'] })

      // Notify parent
      onNewChat?.()
    } catch (error) {
      console.error('[LiveChat] Disconnect error:', error)
      setDisconnectedError('Failed to disconnect. Please try again.')
    } finally {
      setIsDisconnecting(false)
    }
  }, [connection, chatId, appKey, projectId, queryClient, onNewChat])

  const sendMessage = async (userText: string) => {
    setSendError('')
    if (!userText.trim()) return
    if (!connection || connection.state !== HubConnectionState.Connected) {
      setSendError('Not connected')
      return
    }
    if (!chatId) {
      setSendError('Chat not ready')
      return
    }
    const now = new Date().toISOString()
    setMessages((prev) => [...prev, { id: generateRequestId(), role: 'user', content: userText, timestamp: now }])
    // Prepare an assistant placeholder to stream into
    setIsAwaitingAssistant(true)
    const newAssistantId = generateRequestId()
    currentAssistantIdRef.current = newAssistantId
    setMessages((prev) => [...prev, { id: newAssistantId, role: 'assistant', content: '', timestamp: now }])
    setThinkingByMessageId((prev) => ({ ...prev, [newAssistantId]: '' }))
    setResponseStartedById((prev) => ({ ...prev, [newAssistantId]: false }))

    try {
      // Check if we should use message-based flow (new architecture)
      // When sendMessageEventId is provided, send via catalog event handler
      const eventId = connectionData.sendMessageEventId
      if (eventId && appKey && projectId) {
        // New message-based flow: send via catalog event handler
        // The worker will pick up the message and respond via SignalR
        console.log('[LiveChat] Sending message via catalog event handler:', eventId)
        const result = await sendLiveChatMessage({
          appKey,
          projectId,
          // organisationId,
          eventId,
          message: userText,
          chatId,
          filters: [], // TODO: pass current filters
        })
        if (!result.success) {
          setSendError(result.error || 'Failed to send message')
          setIsAwaitingAssistant(false)
        } else {
          console.log('[LiveChat] Message sent via event handler')
        }
      } else {
        // Legacy flow: send directly via SignalR
        await connection.invoke('SendMessageToChat', chatId, userText)
      }
    } catch (_err) {
      console.error('[LiveChat] Error sending message:', _err)
      setSendError('Failed to send message')
      setIsAwaitingAssistant(false)
    }
  }

  // (ReceiveMessage handler is registered in the connection setup effect above)

  return (
    <div ref={ref} className="relative flex h-full flex-col">
      {title && (
        <ComponentHeader emphasize={emphasizeHeader}>
          <ComponentTitle title={title} description={description} />
          <ComponentActionsContainer>{tileFilters}</ComponentActionsContainer>
        </ComponentHeader>
      )}
      <ScrollArea style={{ height: contentHeight }} ref={scrollAreaRef}>
        <div style={{ minHeight: height }} className="mx-auto flex max-w-5xl flex-col items-center justify-center">
          <ChatContainer>
            {/* Connection status banners */}
            {!isMessageBasedFlow && !workerStarted && connectionData.error && (
              <div className="mx-auto my-1 rounded-md border border-destructive bg-destructive/10 px-2 py-1 text-xs text-destructive">
                {connectionData.error}
              </div>
            )}
            {connecting && (
              <div className="mx-auto my-1 inline-flex items-center gap-2 rounded-md border border-border bg-muted px-2 py-1 text-xs">
                <Loader2 className="h-3 w-3 animate-spin" />
                {isMessageBasedFlow ? 'Connecting to chat…' : 'Connecting to chat worker…'}
              </div>
            )}
            {/* Reconnecting state - show while attempting to reconnect */}
            {isReconnecting && (
              <div className="mx-auto my-1 inline-flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-700">
                <Loader2 className="h-3 w-3 animate-spin" />
                Reconnecting{reconnectAttempt > 0 ? ` (attempt ${reconnectAttempt})` : ''}…
                <Button
                  variant="ghost"
                  size="icon"
                  className="ml-1 h-5 w-5 text-amber-700 hover:bg-amber-100 hover:text-amber-800"
                  onClick={refreshConnection}
                  aria-label="Force reconnect"
                  title="Force reconnect now"
                >
                  <RefreshCw className="h-3 w-3" />
                </Button>
              </div>
            )}
            {/* Error state - only show for actual failures, not during reconnection */}
            {!!disconnectedError && connectionStatus === 'failed' && (
              <div className="mx-auto my-1 rounded-md border border-destructive bg-destructive/10 px-2 py-1 text-xs text-destructive">
                {disconnectedError}
              </div>
            )}
            {/* Connected state */}
            {connectionStatus === 'connected' && chatId && (
              <div className="mx-auto my-1 inline-flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs text-emerald-700">
                <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
                Connected
                <Button
                  variant="ghost"
                  size="icon"
                  className="ml-1 h-5 w-5 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800"
                  onClick={refreshConnection}
                  aria-label="Refresh connection"
                  title="Refresh connection"
                >
                  <RefreshCw className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="ml-1 h-5 w-5 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800"
                  onClick={startNewChat}
                  aria-label="Start new chat"
                  title="Start new chat session"
                >
                  <Plus className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="ml-1 h-5 w-5 text-red-600 hover:bg-red-100 hover:text-red-700"
                  onClick={disconnectChat}
                  disabled={isDisconnecting}
                  aria-label="Disconnect"
                  title="Disconnect from chat"
                >
                  {isDisconnecting ? <Loader2 className="h-3 w-3 animate-spin" /> : <LogOut className="h-3 w-3" />}
                </Button>
              </div>
            )}
            {/* Disconnected state - show reconnect button */}
            {connectionStatus === 'disconnected' && (
              <div className="mx-auto my-1 inline-flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-700">
                <span className="inline-block h-2 w-2 rounded-full bg-amber-500" />
                Disconnected
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-1 h-5 gap-1 px-2 text-amber-700 hover:bg-amber-100 hover:text-amber-800"
                  onClick={refreshConnection}
                >
                  <RefreshCw className="h-3 w-3" />
                  Reconnect
                </Button>
              </div>
            )}
            {/* Failed state - show reconnect button with error */}
            {connectionStatus === 'failed' && (
              <div className="mx-auto my-1 flex items-center gap-2">
                <Button variant="outline" size="sm" className="gap-1.5" onClick={refreshConnection}>
                  <RefreshCw className="h-3 w-3" />
                  Reconnect
                </Button>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={startNewChat}>
                  <Plus className="h-3 w-3" />
                  New Chat
                </Button>
              </div>
            )}
            {/* Messages */}
            <LiveChatMessageList
              messages={messages}
              isLoading={isAwaitingAssistant}
              thinkingByMessageId={thinkingByMessageId}
              responseStartedByMessageId={responseStartedById}
              agentStepsByMessageId={agentStepsByMessageId}
            />

            {/* Floating scroll to bottom button */}
            {showScrollButton && (
              <Button
                onClick={() => scrollToBottom(true)}
                variant="outline"
                size="icon"
                className="absolute bottom-[15%] right-1/2 z-10 flex h-10 w-10 translate-x-1/2 items-center justify-center rounded-full shadow-lg transition-all duration-200 hover:scale-105"
                aria-label="Scroll to bottom"
              >
                <ChevronDown className="h-5 w-5" />
              </Button>
            )}

            {/* Error display */}
            {sendError && (
              <div className="border-b border-red-200 bg-red-50 p-3">
                <p className="text-sm text-red-600">Failed to send message. Please try again.</p>
              </div>
            )}

            {/* Input */}
            <LiveChatInput
              samplePrompts={samplePrompts}
              className="bg-background/80 pt-0 backdrop-blur"
              sendMessage={sendMessage}
              isLoading={isAwaitingAssistant}
              disabled={
                !chatId ||
                connecting ||
                isReconnecting ||
                connectionStatus === 'failed' ||
                connectionStatus === 'disconnected'
              }
            />
          </ChatContainer>
        </div>
      </ScrollArea>
    </div>
  )
})
